import dayjs, { Dayjs } from 'dayjs';
import duration, { Duration } from 'dayjs/plugin/duration.js';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter.js';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore.js';

import { TimeZoneRegion } from '../models/serializable/time-zone-region.js';
import { TrueTime } from './true-time.js';
import { kMillisecondsPerMinute } from '../utility/constants.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../utility/null-safety.js';

dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const DEFAULT_REGION: TimeZoneRegion = TimeZoneRegion.EuropeBucharest;
const DAYS_IN_MONTH: number[] = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export class TimeZone {
  private constructor(
    private readonly region: TimeZoneRegion,
  ) {}

  private static timeZoneCache: Map<TimeZoneRegion, TimeZone>;

  static ofRegion(region: TimeZoneRegion): TimeZone {
    //  HACK: Static variables are somehow lost in the browser ¯\_(ツ)_/¯
    this.ensureInitialized();

    const result = this.timeZoneCache?.get(region);
    if(isNullOrUndefined(result)) {
      throw new Error(`Failed to find time zone region in cache: ${region}`);
    }

    return result;
  }

  static default(): TimeZone {
    return this.ofRegion(DEFAULT_REGION);
  }

  now(): dayjs.Dayjs {
    return TrueTime.now().tz(this.region);
  }

  millisecondsSinceEpoch(): number {
    return this.now().valueOf();
  }

  at(
      year: number,
      month = 0,
      day = 1,
      hour = 0,
      minute = 0,
      second = 0,
      millisecond = 0,
  ): dayjs.Dayjs {
    // Parse the wall clock in the target region, independently of the device timezone.
    const wallClock = dayjs.utc(Date.UTC(year, month, day, hour, minute, second, millisecond));
    return dayjs.tz(wallClock.format('YYYY-MM-DD HH:mm:ss.SSS'), this.region);
  }

  atMidnight(date: dayjs.Dayjs): dayjs.Dayjs {
    return this.at(date.year(), date.month(), date.date(), 0, 0, 0, 0);
  }

  atJustBeforeTheEndofTheDay(date: dayjs.Dayjs): dayjs.Dayjs {
    return this.at(date.year(), date.month(), date.date(), 23, 59, 59, 999);
  }

  getDayAtFirstHour(date: dayjs.Dayjs): dayjs.Dayjs {
    return this.at(date.year(), date.month(), date.date());
  }

  getTodayAtFirstHour(): dayjs.Dayjs {
    const now = this.now();
    return this.at(now.year(), now.month(), now.date());
  }

  getNextDayAtFirstHour(date: dayjs.Dayjs): dayjs.Dayjs {
    return this.getFutureDayAtFirstHour(1, date);
  }

  getFutureDayAtFirstHour(daysFromToday: number, today?: dayjs.Dayjs): dayjs.Dayjs {
    today ??= this.getTodayAtFirstHour();

    // Calendar days must not be decomposed into duration months or fixed 24-hour periods.
    const futureDay = dayjs.utc(Date.UTC(today.year(), today.month(), today.date())).add(daysFromToday, 'day');
    return this.at(futureDay.year(), futureDay.month(), futureDay.date());
  }

  getPastDayAtFirstHour(daysFromToday: number, today?: dayjs.Dayjs): dayjs.Dayjs {
    return this.getFutureDayAtFirstHour(-daysFromToday, today);
  }

  getFirstDayOfMonth(date: dayjs.Dayjs): dayjs.Dayjs {
    return this.at(date.year(), date.month(), 1);
  }

  getFutureMonthOnSameDayAtSameTime(monthsFromNow: number, opts: { today: dayjs.Dayjs }): dayjs.Dayjs {
    const today = opts.today ?? this.getTodayAtFirstHour();
    let year: number = today.year();
    let month: number = today.month();
    let day: number = today.date();

    while(monthsFromNow > 0) {
      month++;
      if(month === 12) {
        year++;
        month = 0;
      }

      monthsFromNow--;
    }

    let daysInMonth = DAYS_IN_MONTH[month];

    if(year % 4 === 0 && month === 1) daysInMonth++;
    if(day > daysInMonth) day = daysInMonth;

    return this.at(
        year,
        month,
        day,
        today.hour(),
        today.minute(),
        today.second(),
        today.millisecond(),
    );
  }

  getFutureMonthOnSameDayAtFirstHour(monthsFromNow: number, opts: { today: dayjs.Dayjs }): dayjs.Dayjs {
    const futureMonth = this.getFutureMonthOnSameDayAtSameTime(monthsFromNow, opts);
    return this.at(
        futureMonth.year(),
        futureMonth.month(),
        futureMonth.date(),
    );
  }

  getFutureYearOnSameDayAtSameTime(yearsFromNow: number, opts: { today: dayjs.Dayjs }): dayjs.Dayjs {
    const today = opts.today ?? this.getTodayAtFirstHour();
    const month: number = today.month();

    let year: number = today.year();
    let day: number = today.date();

    while(yearsFromNow > 0) {
      year++;
      yearsFromNow--;
    }

    let daysInMonth = DAYS_IN_MONTH[month];

    if(year % 4 === 0 && month === 1) daysInMonth++;
    if(day > daysInMonth) day = daysInMonth;

    return this.at(
        year,
        month,
        day,
        today.hour(),
        today.minute(),
        today.second(),
        today.millisecond(),
    );
  }

  getFutureYearOnSameDayAtFirstHour(yearsFromNow: number, opts: { today: dayjs.Dayjs }): dayjs.Dayjs {
    const futureMonth = this.getFutureYearOnSameDayAtSameTime(yearsFromNow, opts);
    return this.at(
        futureMonth.year(),
        futureMonth.month(),
        futureMonth.date(),
    );
  }

  getFutureMonthOnFirstDayAtFirstHour(monthsFromNow: number, today?: dayjs.Dayjs): dayjs.Dayjs {
    today ??= this.getTodayAtFirstHour();
    let year = today.year();
    let month = today.month();

    while(monthsFromNow > 0) {
      month++;
      if(month === 12) {
        year++;
        month = 0;
      }

      monthsFromNow--;
    }

    return this.at(year, month);
  }

  getPastMonthOnFirstDayAtFirstHour(monthsFromNow: number, today?: dayjs.Dayjs): dayjs.Dayjs {
    today ??= this.getTodayAtFirstHour();
    let year = today.year();
    let month = today.month();

    while(monthsFromNow > 0) {
      month--;
      if(month === 0) {
        year--;
        month = 11;
      }

      monthsFromNow--;
    }

    return this.at(year, month);
  }

  getTimeDifference(first: dayjs.Dayjs, second: dayjs.Dayjs): Duration {
    const utcOffsetOfFirst = first.utcOffset();
    const utcOffsetOfSecond = second.utcOffset();

    if(utcOffsetOfFirst === utcOffsetOfSecond) {
      return dayjs.duration(first.diff(second));
    }

    const timeZoneDiff = utcOffsetOfFirst - utcOffsetOfSecond;
    const timeDiff = first.diff(second, 'milliseconds') + (timeZoneDiff * kMillisecondsPerMinute);

    return dayjs.duration(timeDiff, 'milliseconds');
  }

  isDateToday(date: dayjs.Dayjs): boolean {
    const now = this.now();
    return now.year() === date.year() && now.month() === date.month() && now.date() === date.date();
  }

  isSameDay(first: dayjs.Dayjs, second: dayjs.Dayjs): boolean {
    return first?.year() === second?.year() &&
      first?.month() === second?.month() &&
      first?.date() === second?.date();
  }

  isSameMonth(first: dayjs.Dayjs, second: dayjs.Dayjs): boolean {
    return first?.year() === second?.year() &&
        first?.month() === second?.month();
  }

  convert(date: Dayjs): Dayjs {
    return date.tz(this.region);
  }

  static isSameDay(first: dayjs.Dayjs, second: dayjs.Dayjs): boolean {
    return first?.year() === second?.year() &&
      first?.month() === second?.month() &&
      first?.date() === second?.date();
  }

  static isSameMonth(first: dayjs.Dayjs, second: dayjs.Dayjs): boolean {
    return first?.year() === second?.year() &&
        first?.month() === second?.month();
  }

  static encodeDate(date: Dayjs): number {
    return date.valueOf();
  }

  static encodeDateOrNull(date: Dayjs | null): number | null {
    return date?.valueOf() ?? null;
  }

  static decodeDate(encodedDate: number): Dayjs {
    return dayjs(encodedDate);
  }

  static decodeDateOrNull(encodedDate: number | null): Dayjs | null {
    return isNotNullOrUndefined(encodedDate) ? dayjs(encodedDate) : null;
  }

  static encodeDuration(duration: Duration): number {
    return duration.asMilliseconds();
  }

  static encodeDurationOrNull(duration: Duration | null): number | null {
    return duration?.asMilliseconds() ?? null;
  }

  static decodeDuration(encodedDuration: number): Duration {
    return dayjs.duration(encodedDuration);
  }

  static decodeDurationOrNull(encodedDuration: number | null): Duration | null {
    return isNotNullOrUndefined(encodedDuration) ? dayjs.duration(encodedDuration) : null;
  }

  static dateToISOString(date: dayjs.Dayjs): string {
    return date.toISOString();
  }

  static dateToISOStringOrNul(date: dayjs.Dayjs | null): string | null {
    return isNotNullOrUndefined(date) ? date.toISOString() : null;
  }

  static dateFromISOString(encodedDate: string): dayjs.Dayjs {
    return dayjs(encodedDate);
  }

  static dateFromISOStringOrNul(encodedDate: string | null): dayjs.Dayjs | null {
    return isNotNullOrUndefined(encodedDate) ? dayjs(encodedDate) : null;
  }

  static cloneDate(date: dayjs.Dayjs): dayjs.Dayjs {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dateAsAny: any = date;

    const timezone = dateAsAny.$x?.$timezone;
    if(timezone) {
      return dayjs.tz(date.valueOf(), timezone);
    }
    else {
      return dayjs(date.valueOf());
    }
  }

  static cloneDateOrNull(date: dayjs.Dayjs | null): dayjs.Dayjs | null {
    if(isNullOrUndefined(date)) {
      return null;
    }

    return this.cloneDate(date);
  }

  static cloneDuration(original: Duration): Duration {
    return dayjs.duration(original.asMilliseconds());
  }

  static cloneDurationOrNull(original: Duration | null): Duration | null {
    if(isNullOrUndefined(original)) {
      return null;
    }

    return dayjs.duration(original.asMilliseconds());
  }

  static ensureInitialized(): void {
    if(!this.timeZoneCache) {
      this.timeZoneCache = new Map<TimeZoneRegion, TimeZone>();

      for(const region of Object.values(TimeZoneRegion)) {
        this.timeZoneCache.set(region, new TimeZone(region));
      }
    }
  }

  static compareInstants(a: dayjs.Dayjs, b: dayjs.Dayjs): number {
    const aValue = a.valueOf();
    const bValue = b.valueOf();

    if(aValue < bValue) {
      return -1;
    }

    if(aValue > bValue) {
      return 1;
    }

    return 0;
  }

  static isSameInstant(a: dayjs.Dayjs, b: dayjs.Dayjs): boolean {
    return this.compareInstants(a, b) === 0;
  }

  static isSameInstantOrNull(a: dayjs.Dayjs | null, b: dayjs.Dayjs | null): boolean {
    if(isNotNullOrUndefined(a) && isNotNullOrUndefined(b)) {
      return this.isSameInstant(a, b);
    }

    return a === b;
  }
}
